/**
 * Search
 */
import { Component, registerComponent } from 'js/component.js';
import { throttle, uuid } from 'js/helpers.js';
const componentName = 'search';

class Search extends Component {
    // Elements
    elements = {
        config: this.element.querySelector(`.${componentName}__configuration`),
        input: this.element.querySelector(`.${componentName}__input`),
        inputOver: this.element.querySelector(`.${componentName}__input-over`),
        globalNav: document.querySelector('header.global-nav')
    };

    // Util & State
    uuid = `search-${uuid().replace(/-/g, '')}`;
    resizeThrottleMS = 100;
    canSearch = false;
    currentTerm = '';

    RASearch = null;
    searchType = null;
    searchLayout = null;
    pageLanguage = document.head.querySelector('[property="language"][content]') ? document.head.querySelector('[property="language"][content]').content : 'en';
    pageCountry = document.head.querySelector('[property="country"][content]') ? document.head.querySelector('[property="country"][content]').content.toLowerCase() : null;
    baseUrl = null;
    queryParameters = null;
    locale = null;
    searchEndpoint = null;
    dropdownWidth = this.elements.input ? `${this.elements.input.offsetWidth}px` : '0';

    /**
     * Update Search Config
     */
    updateSearchConfig = () => {
        const {
            searchType,
            searchLifecyclenew,
            searchLayout,
            searchBaseUrl,
            searchLocale,
            searchLifecyclenewtableview,
            queryParameters
        } = this.elements.config.dataset;
        this.searchType = searchType;
        this.searchLifecycleNew = searchLifecyclenew;
        this.searchLifecycleNewTableView = searchLifecyclenewtableview;
        this.searchLayout = searchLayout;
        this.baseUrl = searchBaseUrl;
        this.queryParameters = queryParameters;
        this.locale = searchLocale;

        // Set Unique ID for the search
        this.elements.input.classList.add(this.uuid);

        // Set Search Endpoint & Update Configs
        this.searchEndpoint = this.baseUrl + '/' + '${site_lang};keyword=${searchQuery}';//eslint-disable-line

        //Append Query Parameters if author fills in the field
        if(this.queryParameters && this.queryParameters.trim() !== '') {
            const qp = this.queryParameters.trim();
            if(qp[qp.length - 1] === ';') this.queryParameters = qp.substring(0, qp.length - 1);
            this.searchEndpoint += `;${this.queryParameters}`;
        }
    };

populateSearchFromURL = () => {
    const isTableViewEnabled = this.searchLifecycleNewTableView === 'true' || this.searchLifecycleNewTableView === true;

    if (!isTableViewEnabled) return;

    try {
        const urlParams = new URLSearchParams(window.location.search);
        const keywordParam = urlParams.get('keyword');

        if (keywordParam) {
            const decodedKeyword = decodeURIComponent(keywordParam.replace(/\+/g, ' '));
            this.elements.input.value = decodedKeyword;
            this.currentTerm = decodedKeyword;
            this.canSearch = true;
            this.elements.inputOver.classList.add('filled');
            this.elements.inputOver.classList.add('hide-label');
        }
    } catch (error) {
        console.error('Error populating search from URL:', error);
    }
};
    /**
     * Focus/Unfocus Search
     * @param {Boolean} directive - true = focus, false = unfocus
     */
    focusSearch = (directive) => {
        if(directive) {
            this.elements.inputOver.classList.add('active');
            this.elements.inputOver.classList.remove('hide-label');
            this.elements.input.focus();
        } else {
            this.elements.inputOver.classList.remove('active');
            if(this.canSearch) this.elements.inputOver.classList.add('hide-label');
        }
    };

    /**
     * Submit Search
     */
    submitSearch = () => {
        // Check for empty term
        if (this.currentTerm.trim() === '') return;

        const escapedSearchTerm = encodeURIComponent(this.currentTerm).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16)}`);

        // Override all conditions if searchLifecycleNewTableView is true
        const isTableViewEnabled = this.searchLifecycleNewTableView === 'true' || this.searchLifecycleNewTableView === true;
        if (isTableViewEnabled) {
            let lsString = this.currentTerm.trim();
            if(lsString.length < 3) return;
            lsString = lsString.replace(/[^a-z0-9\s\-]/gi, '').replace(/[_]/g, '').trim();
            const currentUrl = window.location.href;
            const url = new URL(currentUrl);
            const keywordWithPlus = lsString.replace(/\s+/g, '+');
            url.searchParams.set('keyword', keywordWithPlus);
            if (url.searchParams.has('filters')) {
                url.searchParams.delete('filters');
            }
            let finalUrl = url.toString().replace(/%2B/g, '+');
            window.location = finalUrl;
            return;
        }

        let redirectUrl = this.searchEndpoint.replace('${site_lang}', `ra-${this.pageLanguage}-${document.head.querySelector('[property="country"][content]').getAttribute('content')}`).replace('${searchQuery}', escapedSearchTerm);

        // Brand Search
        if (this.searchType === 'autosuggestDialog') {
            redirectUrl += ';activeTab=Products';
            window.location = redirectUrl;
        }

        // Literature Search
        if (this.searchType === 'search-literature') {
            redirectUrl += ';activeTab=Literature';
            window.location = redirectUrl;
        }

        // Sample Code Search
        if (this.searchType === 'search-samplecode') {
            redirectUrl += ';activeTab=Sample_Code';
            window.location = redirectUrl;
        }

        // Lifecycle Search
        if (this.searchType === 'search-lifecycle') {
            let lsString = this.currentTerm.trim();
            if(lsString.length < 3) return;
            lsString = encodeURIComponent(lsString.replace(/[^a-z0-9\s]/gi, '').replace(/[_\s]/g, ''));
            redirectUrl = `${this.baseUrl}/ra${this.searchLifecycleNew}${this.locale};keyword=${lsString};startIndex=0;activeTab=Products;spellingCorrect=true;facets=;sort=bma;isPLS=true`;
            window.location = redirectUrl;
        }

        // NEW Elastic Search
        if (this.searchType?.indexOf('search-elastic') !== -1) {
            redirectUrl = `/search.html?keyword=${escapedSearchTerm}`;
            if (this.pageCountry === '__') {
                // local
                redirectUrl = `/content/rockwell-automation/global/en${redirectUrl}`;
            } else if (this.pageCountry !== null && this.pageCountry.toLowerCase() !== 'cn') {
                redirectUrl = `/${this.pageLanguage}-${this.pageCountry}${redirectUrl}`;
            }

            // elastic-search-<type>. e.g. elastic-search-products or elastic-search-literature
            const type = this.searchType.split('-')[2];
            if (typeof type !== 'undefined') {
                redirectUrl += `&tab=${type}`;
            }
            window.location = redirectUrl;
        }

        this.focusSearch(false);
    };

    /**
     * On Input Over Click
     */
    onInputOverClick = (e) => {
        const { classList } = e.target;

        const isClearButton = classList.contains('search__clear')
        || classList.contains('search__clear-icon');

        const isSubmitButton = classList.contains('search__desktop-icon')
        || classList.contains('search__submit-icon')
        || classList.contains('search__submit')
        || classList.contains('search__mobile-icon')
        || classList.contains('search__submit-label')
        || classList.contains('search__lifecycle-icon')
        || classList.contains('search__lifecycle-icon-svg');

        if(isClearButton) {
            this.focusSearch(true);
            this.elements.input.value = '';
            this.currentTerm = '';

            const isTableViewEnabled = this.searchLifecycleNewTableView === 'true' || this.searchLifecycleNewTableView === true;
            if (isTableViewEnabled) {
                const url = new URL(window.location.href);
                url.searchParams.delete('keyword');
                window.history.replaceState({}, '', url.toString());
            }
        } else if(isSubmitButton) {
            this.submitSearch();
        } else {
            this.focusSearch(true);
            this.elements.input.click();
        }
    };

    /**
     * On Outside Click
     */
    onOutsideClick = (e) => {
        if(!e.target.closest(`.${componentName}`) && !e.target.closest('.global-nav__search-trigger')) this.focusSearch(false);
    };

    /**
     * On Input Keyup
     */
    onInputKeyUp = (e) => {
        const isEnterKey = e.key === 'Enter';
        const { value } = this.elements.input;
        this.currentTerm = value.trim();

        if (this.currentTerm === '') {
            this.canSearch = false;
            this.elements.inputOver.classList.remove('filled');
        } else {
            this.canSearch = true;
            this.elements.inputOver.classList.add('filled');
        }

        if (isEnterKey) this.submitSearch();
    };

    /**
     * On Window Resize
     */
    onWindowResize = () => this.dropdownWidth = `${this.elements.input.offsetWidth}px`;

    /**
     * Event Listeners
     */
    addEventListeners() {
        window.addEventListener('resize', throttle(this.onWindowResize, this.resizeThrottleMS));
        document.addEventListener('click', this.onOutsideClick);
        this.elements.input.addEventListener('focus', () => this.focusSearch(true));
        this.elements.input.addEventListener('keyup', this.onInputKeyUp);
        this.elements.inputOver.addEventListener('click', this.onInputOverClick);
    }

    /**
     * Initialize Component
     */
    init() {
        if(!this.elements.input || !this.elements.config) return;

        this.updateSearchConfig();
        this.populateSearchFromURL();
        this.addEventListeners();
    }
}

registerComponent(`.${componentName}`, Search);
